import { useEffect, useState } from "react";
import {  FaWallet } from "react-icons/fa";
import { FiSend, FiAlertCircle, FiCheckCircle, FiUser, FiDollarSign } from "react-icons/fi";
import axios from "../api/axios";

const MemberTransfer = ({ isAdminPanel = false }) => {
  const [wallets, setWallets] = useState(null);
  const [recipientId, setRecipientId] = useState("");
  const [walletType, setWalletType] = useState("lbbp");
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(true);
  const [transferring, setTransferring] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [recipientInfo, setRecipientInfo] = useState(null);
  const [searchingRecipient, setSearchingRecipient] = useState(false);

  useEffect(() => {
    if (isAdminPanel) {
      setLoading(false);
      return;
    }

    const fetchWallets = async () => {
      try {
        const res = await axios.get("/share/dashboard-summary");
        setWallets(res.data);
      } catch (err) {
        setError("Failed to load wallet data");
      } finally {
        setLoading(false);
      }
    };

    fetchWallets();
  }, [isAdminPanel]);

  const searchRecipient = async () => {
    if (!recipientId.trim()) {
      setRecipientInfo(null);
      return;
    }

    setSearchingRecipient(true);
    try {
      const res = await axios.get(`/user/search/${recipientId}`);
      if (res.data) {
        setRecipientInfo(res.data);
        setError("");
      } else {
        setRecipientInfo(null);
        setError("Member not found");
      }
    } catch (err) {
      setRecipientInfo(null);
      setError("Member not found. Please check the ID or unique code.");
    } finally {
      setSearchingRecipient(false);
    }
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    // Validation
    if (!recipientId.trim()) {
      setError("Please enter recipient ID");
      return;
    }
    if (!amount) {
      setError("Please enter amount");
      return;
    }

    const amountNum = parseInt(amount);
    if (!amountNum || amountNum <= 0) {
      setError("Please enter a valid amount");
      return;
    }

    if (!isAdminPanel) {
      if (amountNum < 200) {
        setError("Minimum transfer amount is ₹200");
        return;
      }
      if (amountNum % 100 !== 0) {
        setError("Amount must be in multiples of ₹100");
        return;
      }

      const currentWallet = wallets?.[`${walletType}Wallet`] || 0;
      if (amountNum > currentWallet) {
        setError(`Insufficient balance in ${walletType.toUpperCase()} wallet. Available: ₹${currentWallet}`);
        return;
      }
    }

    if (!recipientInfo) {
      setError("Please search and select a valid recipient");
      return;
    }

    setTransferring(true);
    try {
      const payload = isAdminPanel
        ? { recipientId, amount: amountNum, walletType }
        : { recipientId, amount: amountNum, walletType };

      const res = await axios.post("/share/member-transfer", payload);

      setSuccess(
        `✓ Transfer successful! ₹${amountNum} transferred to ${res.data.recipient} (${(res.data.walletType || walletType).toUpperCase()} wallet)`
      );
      setRecipientId("");
      setAmount("");
      setRecipientInfo(null);
      setWalletType("lbbp");

      // Refresh wallet data
      if (!isAdminPanel) {
        const walletRes = await axios.get("/share/dashboard-summary");
        setWallets(walletRes.data);
      }

      setTimeout(() => setSuccess(""), 5000);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || "Transfer failed. Please try again.");
    } finally {
      setTransferring(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-slate-100">
        <div className="animate-pulse text-lg text-slate-600">Loading...</div>
      </div>
    );
  }

  const currentWallet = wallets?.[`${walletType}Wallet`] || 0;

  return (
    <div className={isAdminPanel ? "p-6" : "min-h-screen bg-slate-100 p-6"}>
      <div className={isAdminPanel ? "max-w-2xl" : "max-w-2xl mx-auto"}>
        {/* Header */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-slate-800 flex items-center gap-3">
            <FiSend className="text-blue-600" />
            {isAdminPanel ? "Admin Fund Transfer" : "Transfer to Member"}
          </h2>
          <p className="text-slate-500 mt-2">
            {isAdminPanel
              ? "Admin can transfer funds to any registered user"
              : "Send funds to another verified member"}
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <FiAlertCircle className="text-red-600 text-xl flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-red-800">Error</p>
              <p className="text-red-700 text-sm mt-1">{error}</p>
            </div>
            <button
              onClick={() => setError("")}
              className="text-red-600 hover:text-red-800 ml-auto font-bold text-xl"
            >
              ×
            </button>
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <FiCheckCircle className="text-green-600 text-xl flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-green-800">Success</p>
              <p className="text-green-700 text-sm mt-1">{success}</p>
            </div>
            <button
              onClick={() => setSuccess("")}
              className="text-green-600 hover:text-green-800 ml-auto font-bold text-xl"
            >
              ×
            </button>
          </div>
        )}

        {/* Main Form Card */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-6">
          <form onSubmit={handleTransfer} className="space-y-6">
            {isAdminPanel && (
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-3">Destination Wallet</label>
                <div className="grid grid-cols-2 gap-4">
                  {["lbbp", "puap"].map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setWalletType(type)}
                      className={`p-4 border-2 rounded-lg transition font-semibold ${
                        walletType === type
                          ? "border-blue-500 bg-blue-50 text-blue-800"
                          : "border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-300"
                      }`}
                    >
                      {type === "lbbp" ? "LBBP Wallet" : "PU&AP Wallet"}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {!isAdminPanel && (
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-3">Source Wallet</label>
                <div className="grid grid-cols-2 gap-4">
                  {["lbbp", "puap"].map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setWalletType(type)}
                      className={`p-4 border-2 rounded-lg transition font-semibold ${
                        walletType === type
                          ? "border-blue-500 bg-blue-50 text-blue-800"
                          : "border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-300"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span>{type === "lbbp" ? "LBBP Wallet" : "PU&AP Wallet"}</span>
                        <span className="text-lg">
                          ₹{wallets?.[`${type}Wallet`] || 0}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Recipient Search */}
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-3">Recipient</label>
              <div className="flex flex-col md:flex-row gap-3">
                <div className="flex-1">
                  <input
                    type="text"
                    placeholder="Enter Member ID or Unique Code (e.g., LBBP000001)"
                    value={recipientId}
                    onChange={(e) => setRecipientId(e.target.value)}
                    onBlur={searchRecipient}
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-lg focus:border-blue-500 focus:outline-none"
                  />
                </div>
                <button
                  type="button"
                  onClick={searchRecipient}
                  disabled={!recipientId.trim() || searchingRecipient}
                  className="px-6 py-3 bg-slate-700 text-white font-semibold rounded-lg hover:bg-slate-800 disabled:bg-slate-400 transition"
                >
                  {searchingRecipient ? "Searching..." : "Search"}
                </button>
              </div>
            </div>

            {/* Recipient Info */}
            {recipientInfo && (
              <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-4">
                  <div className="bg-green-200 p-3 rounded-full">
                    <FiUser className="text-green-700 text-2xl" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-green-800">{recipientInfo.name}</p>
                    <p className="text-sm text-green-700">{recipientInfo.uniqueId}</p>
                  </div>
                  <span className="text-green-600 font-bold text-xl">✓</span>
                </div>
              </div>
            )}

            {/* Amount Input */}
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-3">Amount (₹)</label>
              <div className="relative">
                <span className="absolute left-4 top-3 text-2xl text-slate-400">₹</span>
                <input
                  type="number"
                  placeholder={isAdminPanel ? "Enter amount" : "Enter amount (min ₹200, multiples of ₹100)"}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border-2 border-slate-200 rounded-lg focus:border-blue-500 focus:outline-none"
                />
              </div>
              {!isAdminPanel ? (
                <p className="text-xs text-slate-500 mt-2">
                  • Minimum: ₹200 • Multiples of ₹100 • Available: ₹{currentWallet}
                </p>
              ) : (
                <p className="text-xs text-slate-500 mt-2">
                  • Admin direct credit to user's {walletType === "lbbp" ? "LBBP" : "PU&AP"} Wallet
                </p>
              )}
            </div>

            {/* Transfer Summary */}
            {amount && (
              <div className="bg-slate-50 border-2 border-slate-200 rounded-lg p-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Amount</span>
                    <span className="font-bold text-slate-800">₹{parseInt(amount) || 0}</span>
                  </div>
                  {!isAdminPanel && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">From</span>
                      <span className="font-bold text-slate-800">{walletType === "lbbp" ? "LBBP" : "PU&AP"} Wallet</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">To</span>
                    <span className="font-bold text-slate-800">
                      {recipientInfo
                        ? `${recipientInfo.name}'s ${walletType === "lbbp" ? "LBBP" : "PU&AP"} Wallet`
                        : `Recipient's ${walletType === "lbbp" ? "LBBP" : "PU&AP"} Wallet`}
                    </span>
                  </div>
                  <div className="border-t border-slate-300 pt-2 mt-2 flex justify-between">
                    <span className="font-semibold text-slate-700">Total</span>
                    <span className="text-xl font-bold text-blue-600">₹{parseInt(amount) || 0}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={transferring || !recipientInfo || !amount}
              className="w-full py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-bold rounded-lg hover:from-blue-700 hover:to-blue-800 disabled:from-slate-400 disabled:to-slate-400 transition flex items-center justify-center gap-2"
            >
              <FiSend className="text-lg" />
              {transferring ? "Processing Transfer..." : isAdminPanel ? "Add Money to User" : "Send Transfer"}
            </button>
          </form>
        </div>

        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-blue-100 p-2 rounded">
                <FaWallet className="text-blue-600" />
              </div>
              <p className="font-semibold text-slate-800">Instant Transfer</p>
            </div>
            <p className="text-sm text-slate-600">
              Money transfers instantly to recipient's {walletType === "lbbp" ? "LBBP" : "PU&AP"} Wallet
            </p>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-green-100 p-2 rounded">
                <FiDollarSign className="text-green-600" />
              </div>
              <p className="font-semibold text-slate-800">No Limit</p>
            </div>
            <p className="text-sm text-slate-600">Admin can credit any amount to user wallet</p>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-purple-100 p-2 rounded">
                <FiUser className="text-purple-600" />
              </div>
              <p className="font-semibold text-slate-800">Verified Members</p>
            </div>
            <p className="text-sm text-slate-600">Can only transfer to registered platforms members</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemberTransfer;
